import { ShieldCheck, ShieldX } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type TurnstileRenderOptions = {
  sitekey: string;
  action: string;
  theme: "dark" | "light" | "auto";
  size: "normal" | "compact";
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const scriptId = "digital-garden-turnstile-script";
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("TURNSTILE_SCRIPT_FAILED")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("TURNSTILE_SCRIPT_FAILED"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  siteKey: string | null;
  required: boolean;
  action: "checkout" | "support" | "community_write" | "service_booking";
  resetKey: number;
  onToken: (token: string | null) => void;
}

export function TurnstileWidget({ siteKey, required, action, resetKey, onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const previousResetKey = useRef(resetKey);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "verified" | "error">("idle");

  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      onTokenRef.current(null);
      setState(required ? "error" : "idle");
      return;
    }
    let active = true;
    setState("loading");
    void loadTurnstileScript().then(() => {
      if (!active || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme: "dark",
        size: "normal",
        callback: (token) => {
          if (!active) return;
          setState("verified");
          onTokenRef.current(token);
        },
        "expired-callback": () => {
          if (!active) return;
          setState("ready");
          onTokenRef.current(null);
        },
        "error-callback": () => {
          if (!active) return;
          setState("error");
          onTokenRef.current(null);
        },
      });
      setState("ready");
    }).catch(() => {
      if (!active) return;
      setState("error");
      onTokenRef.current(null);
    });
    return () => {
      active = false;
      onTokenRef.current(null);
      if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [action, required, siteKey]);

  useEffect(() => {
    if (previousResetKey.current === resetKey) return;
    previousResetKey.current = resetKey;
    onTokenRef.current(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setState("ready");
    }
  }, [resetKey]);

  if (!siteKey) {
    return required ? <div className="turnstile-state error"><ShieldX size={16}/><span>Human-verification site key is not configured.</span></div> : null;
  }

  return <div className="turnstile-shell" data-state={state}>
    <div ref={containerRef} className="turnstile-widget" />
    <div className="turnstile-caption"><ShieldCheck size={14}/><span>{state === "verified" ? "Human verification complete" : state === "error" ? "Verification could not load" : "Human verification required for this protected action"}</span></div>
  </div>;
}
